
import { EntityLinkType } from "src/services/common.types";
import { Contact } from "src/services/contacts/contacts.types";
import { DocumentFile, DocumentType } from "src/services/files/file.types";
import { ICompanyProfile } from "src/services/connections/connections.types";

import { Currency } from "../utils/utils.types";
import { User } from "../organisation/organisation.types";
import { AttachedThread } from "../threads/threads.types";
import { IJob, JobRecruitmentType } from "../jobs/jobs.types";
import { UpdateCustomFieldValueDto } from "../custom-fields/custom-field.types";

export const DEAL_STORAGE_KEYS = {
  SELECTED_PIPELINE_ID: 'deal_flow_selected_pipeline_id',
};

export enum DealType {
  NEW = 'new',
  EXISTING = 'existing',
}

export enum DealStatus {
  ACTIVE = 'active',
  WON = 'won',
  LOST = 'lost',
  ARCHIVED = 'archived',
}

export enum PipelineStageType {
  OPEN = 'open',
  WON = 'won',
  LOST = 'lost'
}

export interface CreateDealDto {
  title: string;
  description?: string;
  type: DealType;
  value: number;
  close_date?: Date;
  pipeline_id: string;
  stage_id?: string;
  job_title?: string;
  job_description?: string;
  job_vacancies?: number;
  job_recruitment_type?: JobRecruitmentType;
  custom_fields?: UpdateCustomFieldValueDto;
}

export interface UpdateDealDto {
  title?: string;
  description?: string;
  value?: number;
  type?: DealType;
  close_date?: Date;
  stage_id?: string;
  job_title?: string;
  job_description?: string;
  job_vacancies?: number;
  job_recruitment_type?: JobRecruitmentType;
  assigned_to_id?: string;
  custom_fields?: UpdateCustomFieldValueDto;
}


export interface ProgressDealDto {
  stage_id: string;
}


export interface UpdateDealAssigneeDto {
  assigned_to_id: string;
}

export interface CreateDealStageDto {
  identifier?: string;
  name: string;
  type: PipelineStageType;
  order: number;
  completion_rate: number;
  color?: string;
}

export interface CreatePipelineDto {
  name: string;
  currency: string;
  auto_archive_settings: {
    enabled: boolean;
    archive_in_days?: number;
  };
  stages: CreateDealStageDto[];
  departments?: string[];
}

export interface UpdatePipelineDto {
  name: string;
  currency?: string;
  auto_archive_settings: {
    enabled: boolean;
    archive_in_days?: number;
  };
  stages?: CreateDealStageDto[];
  departments?: string[];
}

export interface IDeal {
  id: string;
  title: string;
  description?: string;
  value: number;
  type: DealType;
  close_date?: Date;
  stage: IDealStage;
  job_title?: string;
  job_description?: string;
  job_vacancies?: number;
  job_recruitment_type?: JobRecruitmentType;
  assigned_to?: Partial<User>;
  created_by: Partial<User>;
  thread: AttachedThread;
  pipeline: Partial<IDealPipeline>;
  status: DealStatus;
  job?: Partial<IJob>;
  created_at: Date;
}

export interface IDealPipeline {
  id: string;
  name: string;
  auto_archive_enabled: boolean;
  auto_archive_in_days?: number;
  currency?: Currency;
  stages: IDealStage[];
  departments?: {
    id: string;
    name: string;
  }[];
  created_at: Date;
}

export interface IDealStage {
  id: string;
  name: string;
  order: number;
  type: PipelineStageType;
  completion_rate: number;
  color?: string;
  deal_count?: number;
}

export type DealFilters = {
  search_query?: string;
  assigned_to?: string;
  global?: boolean;
  status?: DealStatus;
  page?: number;
  per_page?: number;
};

export type AddCompanyLinksDto = {
  company_profiles: {
    company_profile_id: string;
    type: EntityLinkType;
  }[]
}

export type CreateDealDocumentDto = {
  file_id: string;
  name: string;
  type: DocumentType;
}

export interface IDealDocument {
  id: string;
  document: DocumentFile;
  created_at: Date;
}

export interface AddContactLinksDto {
  contacts: {
    contact_id: string;
    type: EntityLinkType;
  }[]
}

export interface SaveDocumentToLinkDto {
  link_type: 'company' | 'contact';
  link_id: string;
}

export interface IDealContactLink {
  id: string;
  type: EntityLinkType;
  contact: Contact;
  created_at: Date;
}

export interface IDealCompanyLink {
  id: string;
  company_profile: ICompanyProfile;
  type: EntityLinkType;
  created_at: Date;
}